import React, { useState } from 'react';
import ClientNotesTab from '../components/ClientNotesTab';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeftIcon,
  VideoCameraIcon,
  CheckCircleIcon,
  PlusIcon,
  ClipboardDocumentListIcon,
  ChartBarIcon,
  SparklesIcon,
  CheckIcon,
  BookOpenIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer,
} from 'recharts';

// ── Static dummy data ─────────────────────────────────────────────────────────

const CLIENT = {
  name: 'Jane Smith',
  meetingUrl: 'https://meet.google.com/xyz-abc-def',
};

const SESSION = { name: 'Executive Coaching' };

const PREP_FORM = {
  submittedAt: '2h ago',
  answers: [
    { question: 'Greatest win(s) since our last call', answer: 'I finally finished the project proposal I had been procrastinating on for two weeks. Also made it to the gym 3 times this week which felt huge.' },
    { question: 'What I am grateful for this week', answer: 'My team has been really supportive. My manager gave me positive feedback on a presentation.' },
    { question: 'Commitments made on last call', answer: 'I committed to breaking the project proposal into 30-min blocks and using the Pomodoro method.' },
    { question: 'What happened around those commitments', answer: 'I did use the time blocks. Struggled the first two days but got into the rhythm by Wednesday.' },
    { question: 'Challenges and opportunities I am facing', answer: 'Still feeling overwhelmed when my inbox piles up. Not sure how to prioritize when everything feels urgent.' },
    { question: 'What I want to focus on today', answer: 'Email triage system and deciding what actually needs my attention vs what I can delegate.' },
  ],
};

const ACTION_ITEMS = [
  { id: '1', title: 'Break large projects into 30-min time blocks', dueDate: 'Sep 12', status: 'pending' },
  { id: '2', title: 'Set up a "Daily Priority 3" habit each morning', dueDate: 'Sep 9', status: 'overdue' },
  { id: '3', title: 'Schedule a 1:1 with manager to clarify Q4 priorities', dueDate: 'Sep 15', status: 'pending' },
  { id: '4', title: 'Use the Pomodoro method for deep work blocks', dueDate: 'Ongoing', status: 'in_progress' },
];

const PREVIOUS_OVERVIEW = {
  lastSession: 'Sep 7, 2026',
  coachNote: 'Jane responds best to concrete, time-boxed systems. Abstract frameworks need to be grounded in her current workload before they land. Strong momentum this cycle — celebrate the gym streak before pivoting to email.',
  themes: [
    'Email as anxiety trigger, not a productivity tool',
    'Reactive prioritization — urgency always beats importance',
    'Structured tasks succeed; unstructured ones stall',
  ],
  commitments: [
    { text: 'Pomodoro blocks for deep work', outcome: 'Adopted, holding', held: true },
    { text: 'Daily Priority 3 morning habit', outcome: '3 of 5 days — partial', held: null },
    { text: '1:1 with manager for Q4 alignment', outcome: 'Not completed', held: false },
  ],
};

const PREP_NOTES = {
  client_snapshot: 'A mid-level manager using coaching to address executive functioning challenges that are limiting her effectiveness and causing burnout risk.',
  recent_work: '• Time-blocking: completed overdue project proposal using 30-min Pomodoro blocks\n• Gym routine: 3x per week maintained for first time in months\n• Task initiation: Pomodoro method adopted and holding through Wednesday sessions',
  client_patterns: '• Email overload recurs every session — inbox as an anxiety trigger, not a tool\n• Prioritization remains reactive: urgency always beats importance\n• Strong follow-through on structured tasks; breaks down when structure disappears',
  focus_for_session: '• Build a concrete email triage system she can use daily without willpower\n• Introduce a "decide vs. delegate vs. delete" filter for incoming requests\n• Reconnect urgency/importance matrix to her stated Q4 goals',
};

interface RatingPoint { label: string; value: number; }
interface YesNoPoint { name: string; count: number; }
const CHECKIN_CHARTS = [
  { id: 'tm', question: 'How well did you manage your time this week? (1–5)', type: 'rating' as const, data: [{ label: 'Aug 5', value: 2 }, { label: 'Aug 12', value: 3 }, { label: 'Aug 19', value: 2 }, { label: 'Aug 26', value: 4 }, { label: 'Sep 2', value: 4 }, { label: 'Sep 7', value: 5 }] as RatingPoint[] },
  { id: 'ti', question: 'Did you start tasks without procrastinating most days?', type: 'yesno' as const, data: [{ name: 'Yes', count: 4 }, { name: 'No', count: 2 }] as YesNoPoint[] },
  { id: 'energy', question: 'Rate your energy and focus this week (1–5)', type: 'rating' as const, data: [{ label: 'Aug 5', value: 3 }, { label: 'Aug 12', value: 2 }, { label: 'Aug 19', value: 3 }, { label: 'Aug 26', value: 3 }, { label: 'Sep 2', value: 4 }, { label: 'Sep 7', value: 4 }] as RatingPoint[] },
];
const LAST_CHECKIN = { date: 'Sep 7, 2026', freetext: 'Feeling more in control this week. The time blocks helped a lot. Still struggling with email overwhelm but making progress.' };

// ── Drawer content components (no card wrapper — drawer provides it) ───────────

function openJoinWindow(url: string): void {
  const w = Math.round(window.screen.width * 0.8);
  const h = Math.round(window.screen.height * 0.8);
  const left = Math.round((window.screen.width - w) / 2);
  const top = Math.round((window.screen.height - h) / 2);
  window.open(url, '_blank', `width=${w},height=${h},left=${left},top=${top},resizable=yes,scrollbars=yes`);
}

function BulletList({ text, color }: { text: string; color: string }) {
  return (
    <ul className="space-y-1.5">
      {text.split('\n').filter(Boolean).map((line, i) => (
        <li key={i} className="flex items-start gap-2 text-sm text-gray-300 leading-snug">
          <span className={`${color} mt-0.5 flex-shrink-0`}>•</span>
          <span>{line.replace(/^•\s*/, '')}</span>
        </li>
      ))}
    </ul>
  );
}

function PrepNotesContent() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-300 leading-relaxed italic border-l-2 border-primary-500 pl-3">
        {PREP_NOTES.client_snapshot}
      </p>
      <div>
        <p className="text-xs font-semibold text-primary-300 uppercase tracking-wider mb-2">Recent Work</p>
        <BulletList text={PREP_NOTES.recent_work} color="text-primary-400" />
      </div>
      <div>
        <p className="text-xs font-semibold text-violet-300 uppercase tracking-wider mb-2">Patterns</p>
        <BulletList text={PREP_NOTES.client_patterns} color="text-violet-400" />
      </div>
      <div>
        <p className="text-xs font-semibold text-green-300 uppercase tracking-wider mb-2">Focus for Today</p>
        <BulletList text={PREP_NOTES.focus_for_session} color="text-green-400" />
      </div>
    </div>
  );
}

function PreviousOverviewContent() {
  return (
    <div className="space-y-4">
      <p className="text-sm text-gray-300 leading-relaxed italic border-l-2 border-sky-600 pl-3">
        {PREVIOUS_OVERVIEW.coachNote}
      </p>
      <div>
        <p className="text-xs font-semibold text-sky-300 uppercase tracking-wider mb-2">Key Themes</p>
        <ul className="space-y-1.5">
          {PREVIOUS_OVERVIEW.themes.map((t, i) => (
            <li key={i} className="flex items-start gap-2 text-sm text-gray-300 leading-snug">
              <span className="text-sky-400 mt-0.5 flex-shrink-0">•</span>
              <span>{t}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="text-xs font-semibold text-amber-300 uppercase tracking-wider mb-2">Last Session Commitments</p>
        <ul className="space-y-2">
          {PREVIOUS_OVERVIEW.commitments.map((c, i) => (
            <li key={i} className="flex items-start gap-2.5">
              <span className={`mt-0.5 flex-shrink-0 text-xs font-semibold rounded px-1.5 py-0.5 ${
                c.held === true ? 'bg-green-900/50 text-green-400 border border-green-700/40'
                : c.held === false ? 'bg-red-900/40 text-red-400 border border-red-700/40'
                : 'bg-amber-900/40 text-amber-400 border border-amber-700/40'
              }`}>
                {c.held === true ? 'Done' : c.held === false ? 'Missed' : 'Partial'}
              </span>
              <div>
                <p className="text-sm text-gray-200 leading-snug">{c.text}</p>
                <p className="text-xs text-dark-muted mt-0.5">{c.outcome}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}


function TrendsContent() {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 gap-6">
        {CHECKIN_CHARTS.map(chart => (
          <div key={chart.id}>
            <p className="text-xs text-dark-muted mb-2 leading-snug">{chart.question}</p>
            {chart.type === 'rating' ? (
              <ResponsiveContainer width="100%" height={110}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <LineChart data={chart.data as any[]} margin={{ top: 2, right: 4, bottom: 0, left: -10 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#2d2952" />
                  <XAxis dataKey="label" tick={{ fontSize: 9, fill: '#6b6891' }} />
                  <YAxis domain={[1, 5]} ticks={[1, 2, 3, 4, 5]} tick={{ fontSize: 9, fill: '#6b6891' }} width={14} />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1635', border: '1px solid #2d2952', borderRadius: 8, fontSize: 11 }} />
                  <Line type="monotone" dataKey="value" stroke="#a78bfa" strokeWidth={2} dot={{ r: 2.5, fill: '#a78bfa' }} />
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <ResponsiveContainer width="100%" height={110}>
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                <BarChart data={chart.data as any[]} margin={{ top: 2, right: 4, bottom: 0, left: -10 }}>
                  <XAxis dataKey="name" tick={{ fontSize: 10, fill: '#6b6891' }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 9, fill: '#6b6891' }} width={14} />
                  <Tooltip contentStyle={{ backgroundColor: '#1a1635', border: '1px solid #2d2952', borderRadius: 8, fontSize: 11 }} />
                  <Bar dataKey="count" fill="#a78bfa" radius={[3, 3, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        ))}
      </div>
      <div className="pt-3 border-t border-dark-border">
        <p className="text-sm text-dark-muted italic">"{LAST_CHECKIN.freetext}"</p>
        <p className="text-xs text-dark-muted mt-1">Last check-in: {LAST_CHECKIN.date}</p>
      </div>
    </div>
  );
}

// ── Inline panels ──────────────────────────────────────────────────────────────

function SessionPrepNotesPanel() {
  return (
    <div className="bg-dark-card rounded-2xl border border-primary-700/50 overflow-hidden flex flex-col h-full">
      <div className="px-4 py-3 bg-primary-900/40 border-b border-primary-700/40 flex items-center gap-2 flex-shrink-0">
        <SparklesIcon className="w-4 h-4 text-primary-400" />
        <span className="text-sm font-semibold text-primary-200">AI Session Prep Notes</span>
        <span className="ml-auto text-xs text-dark-muted">Generated 2h ago</span>
      </div>
      <div className="px-4 py-4 overflow-y-auto flex-1">
        <PrepNotesContent />
      </div>
    </div>
  );
}

function ActionItemsPanel() {
  const [items, setItems] = useState(ACTION_ITEMS);
  const [newItem, setNewItem] = useState('');
  const [adding, setAdding] = useState(false);

  function toggleComplete(id: string) {
    setItems(prev => prev.map(item =>
      item.id === id ? { ...item, status: item.status === 'completed' ? 'pending' : 'completed' } : item
    ));
  }

  function addItem() {
    if (!newItem.trim()) return;
    setItems(prev => [...prev, { id: Date.now().toString(), title: newItem.trim(), dueDate: 'TBD', status: 'pending' }]);
    setNewItem('');
    setAdding(false);
  }

  const textColor = (s: string) => {
    if (s === 'completed') return 'text-dark-muted line-through';
    if (s === 'overdue') return 'text-red-400';
    if (s === 'in_progress') return 'text-primary-300';
    return 'text-gray-200';
  };

  const dueBadge = (item: typeof ACTION_ITEMS[0]) => {
    if (item.status === 'completed') return null;
    const cls = item.status === 'overdue'
      ? 'bg-red-900/40 text-red-400 border border-red-700/40'
      : 'bg-dark-hover text-dark-muted border border-dark-border';
    return <span className={`text-xs px-2 py-0.5 rounded-full ${cls} flex-shrink-0`}>{item.dueDate}</span>;
  };

  return (
    <div className="bg-dark-card rounded-2xl border border-dark-border p-4 flex flex-col gap-2.5 h-full">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <CheckCircleIcon className="w-4 h-4 text-violet-400" />
          <span className="text-sm font-semibold text-white">Open Action Items</span>
        </div>
        <span className="text-xs text-dark-muted">from prior sessions</span>
      </div>

      <div className="flex flex-col gap-2.5 flex-1">
        {items.map(item => (
          <div key={item.id} className="flex items-start gap-2.5">
            <button
              onClick={() => toggleComplete(item.id)}
              className={`mt-0.5 w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                item.status === 'completed' ? 'bg-primary-600 border-primary-600' : 'border-dark-muted hover:border-primary-400'
              }`}
            >
              {item.status === 'completed' && <CheckIcon className="w-2.5 h-2.5 text-white" />}
            </button>
            <span className={`text-sm flex-1 leading-snug ${textColor(item.status)}`}>{item.title}</span>
            {dueBadge(item)}
          </div>
        ))}
      </div>

      {adding ? (
        <div className="flex gap-2">
          <input
            autoFocus
            value={newItem}
            onChange={e => setNewItem(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') addItem(); if (e.key === 'Escape') setAdding(false); }}
            placeholder="New action item…"
            className="flex-1 text-sm px-3 py-1.5 bg-dark-hover border border-primary-600 rounded-lg focus:outline-none focus:ring-1 focus:ring-primary-500 text-white placeholder-dark-muted"
          />
          <button onClick={addItem} className="px-3 py-1.5 bg-primary-600 text-white text-sm rounded-lg hover:bg-primary-700">Add</button>
          <button onClick={() => setAdding(false)} className="px-2 py-1.5 text-dark-muted text-sm hover:text-gray-300">✕</button>
        </div>
      ) : (
        <button
          onClick={() => setAdding(true)}
          className="flex items-center gap-1.5 text-sm text-primary-400 hover:text-primary-300 transition-colors"
        >
          <PlusIcon className="w-3.5 h-3.5" /> Add item
        </button>
      )}
    </div>
  );
}

// ── Drawer ─────────────────────────────────────────────────────────────────────

// ── Inline Session Prep Form card (always expanded when submitted) ─────────────

function SessionPrepFormCard() {
  return (
    <div className="bg-dark-card rounded-2xl border border-dark-border overflow-hidden flex flex-col">
      <div className="px-4 py-3 border-b border-dark-border flex items-center gap-2 flex-shrink-0">
        <ClipboardDocumentListIcon className="w-4 h-4 text-green-400" />
        <span className="text-sm font-semibold text-white">Session Prep Form</span>
        <span className="ml-auto text-xs px-2 py-0.5 bg-green-900/50 text-green-400 rounded-full border border-green-700/40">
          Submitted {PREP_FORM.submittedAt}
        </span>
      </div>
      <div className="flex-1 overflow-y-auto p-4 space-y-3">
        {PREP_FORM.answers.map((item, i) => (
          <div key={i}>
            <p className="text-xs font-semibold text-green-400 uppercase tracking-wide mb-0.5">{item.question}</p>
            <p className="text-sm text-gray-200 leading-snug">{item.answer}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Drawer ─────────────────────────────────────────────────────────────────────

type DrawerKey = 'prev' | 'trends';

const DRAWER_META: Record<DrawerKey, { label: string; subtitle?: string; icon: React.ReactNode; content: React.ReactNode }> = {
  prev: {
    label: 'Previous Session Overview',
    subtitle: PREVIOUS_OVERVIEW.lastSession,
    icon: <BookOpenIcon className="w-4 h-4 text-sky-400" />,
    content: <PreviousOverviewContent />,
  },
  trends: {
    label: 'Check-in Trends',
    subtitle: `Last: ${LAST_CHECKIN.date}`,
    icon: <ChartBarIcon className="w-4 h-4 text-sky-400" />,
    content: <TrendsContent />,
  },
};

// ── Main page ─────────────────────────────────────────────────────────────────

export default function SessionDashboardPage() {
  const navigate = useNavigate();
  const [activeDrawer, setActiveDrawer] = useState<DrawerKey | null>(null);

  const now = new Date();
  const dateLabel = now.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });
  const timeLabel = now.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

  const toggleDrawer = (key: DrawerKey) =>
    setActiveDrawer(prev => (prev === key ? null : key));

  return (
    <div className="min-h-screen bg-dark-canvas flex flex-col">

      {/* ── Top bar ── */}
      <header className="bg-dark-sidebar border-b border-dark-border sticky top-0 z-10 shadow-lg flex items-stretch">
        <div className="flex-shrink-0 border-r border-dark-border bg-white">
          <img
            src="/coachsight-logo.png"
            alt="CoachSight ECR"
            style={{ width: 224, height: 72, objectFit: 'cover', objectPosition: 'center 50%' }}
          />
          <span className="block w-full text-center text-purple-600 text-[11px] font-bold tracking-widest uppercase py-1 border-t border-gray-100">
            See Beyond the Session
          </span>
        </div>

        <div className="flex items-center px-5 flex-1 min-w-0 gap-4">
          <button
            onClick={() => navigate('/dashboard/coach')}
            className="flex items-center gap-1.5 text-sm text-dark-muted hover:text-white transition-colors flex-shrink-0"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            Dashboard
          </button>

          <div className="flex-1 flex flex-col items-center justify-center min-w-0">
            <p className="text-xl font-bold text-white leading-tight">{CLIENT.name}</p>
            <p className="text-xs text-dark-muted leading-tight mt-0.5">
              <span className="text-primary-400 font-medium">{SESSION.name}</span>
              <span className="mx-1.5 opacity-40">·</span>
              <span>{dateLabel}</span>
              <span className="mx-1.5 opacity-40">·</span>
              <span>{timeLabel}</span>
            </p>
          </div>

          <button
            onClick={() => openJoinWindow(CLIENT.meetingUrl)}
            className="flex items-center gap-2 px-5 py-2.5 bg-green-600 hover:bg-green-700 text-white text-sm font-semibold rounded-xl transition-colors flex-shrink-0 shadow-sm"
          >
            <VideoCameraIcon className="w-4 h-4" />
            Join Meeting
          </button>
        </div>
      </header>

      {/* ── Body ── */}
      <div className="flex-1 flex overflow-hidden">

        {/* ── Left area: prep notes + action items + drawer overlay ── */}
        <div className="flex-1 flex flex-col min-w-0 relative overflow-hidden">

          {/* Drawer trigger toolbar */}
          <div className="flex items-center gap-2 px-4 pt-3 pb-2 flex-shrink-0">
            {(Object.entries(DRAWER_META) as [DrawerKey, typeof DRAWER_META[DrawerKey]][]).map(([key, meta]) => (
              <button
                key={key}
                onClick={() => toggleDrawer(key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  activeDrawer === key
                    ? 'bg-dark-hover border-primary-600 text-white'
                    : 'border-dark-border text-dark-muted hover:text-gray-300 hover:border-dark-muted'
                }`}
              >
                {meta.icon}
                {meta.label.split(' ').slice(0, 2).join(' ')}
              </button>
            ))}
          </div>

          {/* Main cards */}
          <div className="flex-1 flex gap-3 p-4 pt-2 pb-4 overflow-hidden" style={{ minHeight: 0 }}>
            {/* Left: AI Prep Notes — full height */}
            <div className="flex-1 flex flex-col min-w-0">
              <SessionPrepNotesPanel />
            </div>
            {/* Right: Prep Form (top, scrollable) + Action Items (below) */}
            <div className="flex-1 flex flex-col gap-3 min-w-0">
              <div className="flex-1 overflow-hidden flex flex-col" style={{ minHeight: 0 }}>
                <SessionPrepFormCard />
              </div>
              <div className="flex-shrink-0">
                <ActionItemsPanel />
              </div>
            </div>
          </div>

          {/* Drawer overlay — slides in from left, covers main cards */}
          <div
            className={`absolute inset-0 z-20 flex flex-col bg-dark-canvas transition-transform duration-300 ease-in-out ${
              activeDrawer ? 'translate-x-0' : '-translate-x-full'
            }`}
          >
            {activeDrawer && (
              <>
                {/* Drawer header */}
                <div className="flex items-center gap-3 px-4 py-3 border-b border-dark-border bg-dark-sidebar flex-shrink-0">
                  {DRAWER_META[activeDrawer].icon}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-semibold text-white">{DRAWER_META[activeDrawer].label}</p>
                    {DRAWER_META[activeDrawer].subtitle && (
                      <p className="text-xs text-dark-muted">{DRAWER_META[activeDrawer].subtitle}</p>
                    )}
                  </div>
                  <button
                    onClick={() => setActiveDrawer(null)}
                    className="p-1.5 rounded-lg text-dark-muted hover:text-white hover:bg-dark-hover transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Drawer content */}
                <div className="flex-1 overflow-auto p-4">
                  {DRAWER_META[activeDrawer].content}
                </div>
              </>
            )}
          </div>
        </div>

        {/* ── Right column: coach notes, always visible ── */}
        <div className="w-80 flex-shrink-0 border-l border-dark-border flex flex-col bg-dark-card overflow-hidden">
          <ClientNotesTab coachClientId="0" onGoToSessions={() => {}} />
        </div>
      </div>
    </div>
  );
}
